// src/pages/ProfilePage.jsx
import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { currentUserApi } from "../api/authApi"; // same API as app

const LANG_KEY = "wr.lang";

const T = {
  header: { en: "Profile", hi: "प्रोफ़ाइल" },
  loading: { en: "Loading profile…", hi: "प्रोफ़ाइल लोड हो रही है…" },
  loadErr: { en: "Unable to load profile.", hi: "प्रोफ़ाइल लोड नहीं हो सकी।" },
  fields: {
    name: { en: "Name", hi: "नाम" },
    email: { en: "Email", hi: "ईमेल" },
    role: { en: "Role", hi: "भूमिका" },
    phone: { en: "Phone", hi: "फ़ोन" },
    address: { en: "Address", hi: "पता" },
  },
};

const ROLE_LABEL = {
  USER: { en: "USER", hi: "यूज़र" },
  OWNER: { en: "OWNER", hi: "ओनर" },
  DRIVER: { en: "DRIVER", hi: "ड्राइवर" },
};

function safe(val) {
  return val && String(val).trim().length > 0 ? val : "—";
}

// 🔹 Avatar text: simple first letter from name (fallback email)
function avatarLetter(me) {
  const src = (me?.userName || me?.email || "").trim();
  const first = src.charAt(0);
  return first ? first.toUpperCase() : "U";
}

/* ───────── Pure display item (no form box) ───────── */
function InfoItem({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5 text-sm">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
        {label}
      </div>
      <div className="text-[13px] sm:text-[14px] font-semibold text-slate-900 leading-relaxed">
        {safe(value)}
      </div>
    </div>
  );
}

export default function ProfilePage() {
  const [lang, setLang] = React.useState("en"); // "en" | "hi"
  const [me, setMe] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");

  // read language once (same key as app)
  React.useEffect(() => {
    try {
      const saved = window.localStorage.getItem(LANG_KEY);
      if (saved === "hi" || saved === "en") setLang(saved);
    } catch {
      // ignore
    }
  }, []);

  const load = React.useCallback(async () => {
    setErr("");
    setLoading(true);
    try {
      const data = await currentUserApi(); // ✅ same backend call as app

      const mapped = {
        email: data?.email,
        userName: data?.userName ?? null,
        role: data?.role ?? "USER",
        phoneNumber: data?.phoneNumber ?? null,
        address: data?.address ?? null,
        district: data?.district ?? null,
        state: data?.state ?? null,
        pinCode: data?.pinCode ?? null,
      };

      setMe(mapped);
    } catch (e) {
      const msg =
        (e && e.message) || T.loadErr[lang] || T.loadErr.en;
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }, [lang]);

  React.useEffect(() => {
    load();
  }, [load]);

  const roleText =
    me?.role && ROLE_LABEL[me.role]
      ? ROLE_LABEL[me.role][lang] || ROLE_LABEL[me.role].en
      : "USER";

  const avatar = avatarLetter(me);

  // 🔹 Combined address string (single display field)
  const fullAddress = [
    me?.address,
    me?.district,
    me?.state,
    me?.pinCode,
  ]
    .map((p) => (p && String(p).trim()) || "")
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <Header />

      <main className="min-h-screen bg-slate-50">
        <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
          {/* Page title */}
          <div className="mb-6 flex items-center justify-between">
            <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">
              {T.header[lang]}
            </h1>
          </div>

          {/* Centered card on large screens */}
          <div className="flex justify-center">
            <section className="w-full max-w-3xl rounded-[28px] border border-slate-200 bg-white shadow-sm backdrop-blur-sm overflow-hidden">
              {/* subtle brand strip */}
              <div
                className="h-[3px] w-full"
                style={{ background: "var(--brand-gradient)" }}
              />

              {/* Identity area */}
              <div className="px-5 sm:px-6 py-4 sm:py-5 flex items-center gap-4 border-b border-slate-100">
                {/* Avatar */}
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white text-lg font-bold shadow-sm">
                  {avatar}
                </div>

                {/* Primary identity */}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-base sm:text-lg font-semibold text-slate-900">
                      {safe(me?.userName)}
                    </p>
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600">
                      {roleText}
                    </span>
                  </div>
                  <p className="mt-0.5 truncate text-xs sm:text-sm text-slate-500">
                    {safe(me?.email)}
                  </p>
                </div>
              </div>

              {/* Content */}
              <div className="px-5 sm:px-6 py-5 sm:py-6">
                {loading ? (
                  <div className="flex items-center gap-3 text-sm text-slate-600">
                    <div className="h-4 w-4 animate-spin rounded-full border-[2px] border-slate-300 border-t-slate-900" />
                    <span>{T.loading[lang]}</span>
                  </div>
                ) : (
                  <>
                    {err && (
                      <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-xs sm:text-sm font-semibold text-rose-800 flex items-center justify-between gap-3">
                        <span>{err}</span>
                        <button
                          type="button"
                          onClick={load}
                          className="shrink-0 rounded-full border border-rose-200 bg-white/80 px-2.5 py-1 text-[11px] font-semibold text-rose-800 hover:bg-rose-50 transition"
                        >
                          Retry
                        </button>
                      </div>
                    )}

                    {/* Section label */}
                    <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Account details
                    </div>

                    {/* Simple 2-column info grid, NOT form-like */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
                      <InfoItem
                        label={T.fields.name[lang]}
                        value={me?.userName}
                      />
                      <InfoItem
                        label={T.fields.email[lang]}
                        value={me?.email}
                      />
                      <InfoItem
                        label={T.fields.phone[lang]}
                        value={me?.phoneNumber}
                      />
                      <InfoItem
                        label={T.fields.role[lang]}
                        value={roleText}
                      />
                      {/* Address takes full width on its own row */}
                      <div className="sm:col-span-2">
                        <InfoItem
                          label={T.fields.address[lang]}
                          value={fullAddress}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}
